import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import s from './styles.module.scss'

import { SubscribeFinalizedModalFormProps } from '../../login.types'
import { SsoLogin } from '../../sso-login/SsoLogin'
import { breakpoints, useMediaQuery } from '../../../../../lib/hooks'
import { Button, Icon } from '../../../../atoms'
import { InputText } from '../../../../molecules'

export interface InputsSubscribeFinalized {
  email: string
  firstname: string
  lastname: string
  password: string
}

export const SubscribeFinalizedModalForm = ({
  isLoading,
  control,
  isFormValid,
  user,
  handleOnSubmit,
  ssoOptions,
}: SubscribeFinalizedModalFormProps) => {
  const intl = useIntl()
  const isMobile = useMediaQuery(breakpoints.newMobile)

  if (!user) {
    throw new Error('User is required')
  }

  return (
    <>
      <form className={s.form}>
        <div className={s.formWrapper}>
          <InputText
            control={control}
            name='email'
            label={
              <FormattedMessage
                id='ui.subscribeFinalizedModalForm.email.label'
                defaultMessage='E-mail professionnel'
              />
            }
            labelProps={{
              'aria-required': 'true',
              className: s.inputTitleSize,
            }}
            rules={{
              value: user.email,
            }}
            inputProps={{
              type: 'text',
              'aria-label': intl.formatMessage({
                id: 'ui.subscribeFinalizedModalForm.email.ariaLabel',
                defaultMessage: 'entrez votre e-mail',
              }),
              'aria-required': 'true',
              className: s.inputBorderRadius,
            }}
            placeholder={user.email ?? ''}
            disabled={true}
            customLabel={
              <div className={s.customLabel}>
                <Icon name='check-circle-outline' color='#0CBC8B' size={16} />
                <FormattedMessage
                  id='ui.subscribeFinalizedModalForm.emailValidated'
                  defaultMessage='Adresse mail validée'
                />
              </div>
            }
          />

          <div className={s.inputNameContainer}>
            <div style={{ width: isMobile ? '100%' : '50%' }}>
              <InputText
                control={control}
                name='firstname'
                label={
                  <FormattedMessage
                    id='ui.subscribeFinalizedModalForm.firstname'
                    defaultMessage='Prénom'
                  />
                }
                rules={{ required: 'Prénom requis.' }}
                labelProps={{ 'aria-required': 'true' }}
                inputProps={{
                  type: 'text',
                  'aria-label': intl.formatMessage({
                    id: 'ui.subscribeFinalizedModalForm.firstname.ariaLabel',
                    defaultMessage: 'Votre prénom',
                  }),
                  'aria-required': 'true',
                  className: s.inputBorderRadius,
                }}
                placeholder={intl.formatMessage({
                  id: 'ui.subscribeFinalizedModalForm.firstname.placeholder',
                  defaultMessage: 'Votre prénom',
                })}
              />
            </div>
            <div style={{ width: isMobile ? '100%' : '50%' }}>
              <InputText
                control={control}
                name='lastname'
                label={
                  <FormattedMessage
                    id='ui.subscribeFinalizedModalForm.name'
                    defaultMessage='Nom'
                  />
                }
                labelProps={{ 'aria-required': 'true' }}
                rules={{
                  required: intl.formatMessage({
                    id: 'ui.subscribeFinalizedModalForm.name.required',
                    defaultMessage: 'Nom requis.',
                  }),
                }}
                inputProps={{
                  type: 'text',
                  'aria-label': intl.formatMessage({
                    id: 'ui.subscribeFinalizedModalForm.name.ariaLabel',
                    defaultMessage: 'Votre nom',
                  }),
                  'aria-required': 'true',
                  className: s.inputBorderRadius,
                }}
                placeholder={intl.formatMessage({
                  id: 'ui.subscribeFinalizedModalForm.name.placeholder',
                  defaultMessage: 'Votre nom',
                })}
              />
            </div>
          </div>

          {!user.isPasswordFilled && (
            <div style={{ paddingTop: isMobile ? 4 : 0 }}>
              <InputText
                control={control}
                name='password'
                label={
                  <FormattedMessage
                    id='ui.subscribeFinalizedModalForm.password'
                    defaultMessage='Mot de passe'
                  />
                }
                labelProps={{
                  'aria-required': 'true',
                }}
                rules={{
                  validate: (password: string | undefined) =>
                    password && password.length >= 6
                      ? true
                      : intl.formatMessage({
                          id: 'ui.subscribeFinalizedModalForm.password.required',
                          defaultMessage: 'Minimum 6 caractères',
                        }),
                }}
                inputProps={{
                  type: 'password',
                  'aria-label': intl.formatMessage({
                    id: 'ui.subscribeFinalizedModalForm.password.ariaLabel',
                    defaultMessage: 'entrez votre mot de passe',
                  }),
                  'aria-required': 'true',
                  className: s.inputBorderRadius,
                }}
                placeholder={intl.formatMessage({
                  id: 'ui.subscribeFinalizedModalForm.password.placeholder',
                  defaultMessage: 'Choisir votre mot de passe',
                })}
              />
            </div>
          )}
        </div>

        <div className={s.footer}>
          <Button
            id='finish-subscribe'
            className={s.loginBtn}
            loading={isLoading}
            onClick={handleOnSubmit}
            disabled={!isFormValid}
            fullWidth={true}
          >
            <span>
              <FormattedMessage
                id='ui.subscribeFinalizedModalForm.validate'
                defaultMessage='Valider'
              />
            </span>
          </Button>

          <SsoLogin {...ssoOptions} />

          {!user.isPasswordFilled && (
            <p className={s.termAndConditions}>
              <FormattedMessage
                id='ui.subscribeFinalizedModalForm.termAndConditions'
                defaultMessage='En cliquant sur Accepter et s’inscrire, vous acceptez les Conditions Générales d’Utilisation, la Politique de confidentialité de Naboo.'
              />
            </p>
          )}
        </div>
      </form>
    </>
  )
}
